/**
 * AI chat functions specific to the Creative Studio context.
 *
 * Only the functions relevant for the Creative Studio are sent to OpenAI,
 * instead of the full 30+ layout-editor functions from aiChatFunctions.js.
 */
export default [
  {
    name: "redirect_to_design_gallery",
    description:
      "Open the Design Gallery for browsing templates. Use for flipbook templates or general template browsing. " +
      "For PDP image set creation, use create_pdp_image_set instead.",
    parameters: {
      type: "object",
      properties: {
        type_filter: {
          type: "string",
          enum: ["pdp", "flipbooks"],
          description: "Use pdp for PDP template sets; flipbooks for flipbook templates (type_flipbooks tag).",
        },
      },
      required: ["type_filter"],
    },
  },
  {
    name: "redirect_to_pdp_images",
    description:
      "Redirect to the PDP Images editor (Image Editor) for scoring images, 3D renders, the photoshoot tool, " +
      "removing the background (transparent cut-out), resizing / cropping / changing the aspect ratio or extending the canvas, " +
      "when the user explicitly asks to open the Image Editor, or when they ask to edit an image WITHOUT saying what to change. " +
      "For a concrete change to an image (text, label, color, remove/add something) use edit_image instead — it edits inline. " +
      "Do NOT use for PDP image sets from templates — use redirect_to_design_gallery with type_filter pdp. " +
      "Do NOT use for banner campaigns or template layouts.",
    parameters: {
      type: "object",
      properties: {
        import_type: {
          type: "string",
          enum: ["3d-image-edit", "3d-renders", "photoshoot"],
          description:
            "'3d-image-edit' for scoring images, opening the Image Editor, or editing without a concrete change; " +
            "'3d-renders' for creating 3D renders of products; " +
            "'photoshoot' ONLY when the user explicitly asks to open the Photo Studio / virtual photoshoot tool. " +
            "Do NOT use 'photoshoot' for 'place this product on <scene/background>' prompts — use place_product_on_background instead.",
        },
      },
      required: ["import_type"],
    },
  },
  {
    name: "edit_image",
    description:
      "Edit an image INLINE in the chat (do NOT redirect) when the user describes a concrete change to it: " +
      "change or replace text or a label, recolor, remove or add an element, recolor the background, fix something. " +
      "Examples: 'Change the label on this wine bottle to read \"John Smith\" instead of \"Robert Mondavi\"', " +
      "'Change the label on this image from \"FIREFLY RIDGE\" to \"FIREFLY HILL\"', 'Remove the price sticker', 'Make the cap gold'. " +
      "The image is the one the user attached, pasted or selected — or, when none is attached, the last image you produced in this chat. " +
      "Do NOT use for removing the background, resizing, cropping, changing the aspect ratio or extending the canvas, " +
      "scoring, 3D renders, photoshoots or opening the Image Editor — use redirect_to_pdp_images for those. Also NOT for " +
      "placing a product on a background (place_product_on_background) or translating an image (translate_image).",
    parameters: {
      type: "object",
      properties: {
        instruction: {
          type: "string",
          description:
            "The change to apply, in the user's words. Keep any quoted text EXACTLY as written (spelling, case, punctuation).",
        },
        image_index: {
          type: "integer",
          description: "0-based index of the user's attachment to edit when they attached more than one. Omit otherwise.",
        },
      },
      required: ["instruction"],
    },
  },
  {
    name: "split_image_into_layers",
    description:
      "Split an attached, pasted, selected, or recently generated image into separate visual layers. " +
      "Use when the user asks to layer, split, separate, decompose, or extract layers from an image. " +
      "Examples: 'Please layer this image', 'Split this into layers', 'Separate the background and objects'. " +
      "Do not use for normal image editing or background removal.",
    parameters: {
      type: "object",
      properties: {
        image_index: {
          type: "integer",
          description: "0-based attachment index when the user attached multiple images. Omit otherwise.",
        },
      },
      required: [],
    },
  },
  {
    name: "place_product_on_background",
    description:
      "Place a single product onto a background or scene, INLINE in the chat (do NOT redirect). " +
      "Use for prompts like 'Place this product on a kitchen counter', 'Put B08T2WP81T on a summer beach', " +
      "'Place this product on this background', 'Create this product image on a summer beach background'. " +
      "The product may be an attached/pasted/selected image OR an identifier (ASIN/GTIN/name/Salsify) whose " +
      "hero image will be fetched. " +
      "TWO CASES: " +
      "(1) The user describes the scene in WORDS (no background image) → set `background_scene` to the scene " +
      "description only (no product/brand names). " +
      "(2) The user provides a background IMAGE (attached/pasted/selected) → set `background_reference` to that " +
      "image and DO NOT set `background_scene`. " +
      "NEVER set both `background_scene` and `background_reference` in the same call. " +
      "When the user attaches BOTH a product image and a background image, put the product in `product_references` " +
      "and the background in `background_reference`, using distinct attachment indices. " +
      "Do NOT use redirect_to_pdp_images for these prompts.",
    parameters: {
      type: "object",
      properties: {
        product_references: {
          type: "array",
          maxItems: 1,
          description: "The single product to place. Use the first/primary product the user mentions.",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: ["gtin", "asin", "name", "attachment", "url", "salsify"],
                description:
                  "'gtin' for numeric product IDs (12-14 digits), 'asin' for Amazon identifiers " +
                  "(B + 9 alphanumeric), 'name' for brand/product names, 'attachment' for a user-attached " +
                  "image (value = attachment index), 'url' for a direct image URL, 'salsify' for Salsify IDs.",
              },
              value: {
                type: "string",
                description:
                  "ONLY the bare identifier — the GTIN digits, ASIN code, brand/product name, attachment index, or URL. " +
                  "MUST NOT contain the surrounding prompt or words like 'place', 'product', 'background', 'on'. " +
                  "Example: for 'Place B08T2WP81T on a beach' the value is 'B08T2WP81T'.",
              },
              display_name: {
                type: "string",
                description: "Human-readable label. Same rules as `value` — never the full prompt.",
              },
            },
            required: ["type", "value"],
          },
        },
        background_scene: {
          type: "string",
          description:
            "Case 1 only. A description of the background scene/environment ONLY (no product or brand names). " +
            "You MAY enrich this with helpful visual detail (lighting, materials, mood, depth of field) to guide image generation. " +
            "Example: 'a kitchen counter', 'a summer beach at sunset'. Leave unset when the user provides a background image.",
        },
        user_scene_phrase: {
          type: "string",
          description:
            "Case 1 only. The scene EXACTLY as the user phrased it, in their own words and nothing more " +
            "(e.g. user says 'place this product on a kitchen counter' → 'a kitchen counter'). " +
            "Do NOT enrich, expand, or add any visual detail here — this short phrase is shown back to the user verbatim. " +
            "Leave unset when the user provides a background image.",
        },
        background_reference: {
          type: "object",
          description:
            "Case 2 only. The user-provided background image. Leave unset when the user describes the scene in words.",
          properties: {
            type: {
              type: "string",
              enum: ["attachment", "url"],
              description: "'attachment' for a user-attached/pasted/selected image (value = attachment index), 'url' for a direct image URL.",
            },
            value: {
              type: "string",
              description: "The attachment index (for type 'attachment') or the image URL (for type 'url').",
            },
          },
          required: ["type", "value"],
        },
        image_prompt: {
          type: "string",
          description:
            "The full original user prompt for reference and additional placement guidance. " +
            "The structured fields (product_references, background_scene, background_reference) are the decomposed version used for generation.",
        },
      },
      required: ["product_references"],
    },
  },
  {
    name: "create_campaign",
    description:
      "Use when the user mentions a specific campaign or network name " +
      "(e.g., 'Create a Walmart Connect campaign', 'Instacart ads placement set', 'New Meta set'). " +
      "Returns the matched network name so the system can resolve placement types and sizes. " +
      "If the user also specifies a placement type (e.g., 'Walmart Connect Onsite', 'offsite campaign'), include it in placement_type. " +
      "Do NOT use create_template when the user references a known network name — use this function instead.",
    parameters: {
      type: "object",
      properties: {
        network_name: {
          type: "string",
          description:
            "The network or campaign name the user mentioned (e.g., 'Walmart Connect', 'Meta', 'Instacart Ads').",
        },
        placement_type: {
          type: "string",
          description:
            "The placement type the user specified, if any (e.g., 'Onsite', 'Offsite', 'Feed'). " +
            "Only include this if the user explicitly mentioned a placement type in their prompt.",
        },
        image_prompt: {
          type: "string",
          description: "The full original user prompt for reference. The structured fields (background_scene, product_references, text_elements) are the decomposed version used for generation.",
        },
        background_scene: {
          type: "string",
          description:
            "A description of the background scene/environment ONLY. " +
            "Must NOT contain product names, brand names, or any specific product imagery. " +
            "Focus on theme, mood, colors, setting, atmosphere. " +
            "Example: 'A vibrant autumn football tailgate scene with warm orange and brown tones, stadium in background, scattered fallen leaves'.",
        },
        product_references: {
          type: "array",
          description: "Products the user explicitly wants FEATURED as separate layers. "
            + "Extract ONLY concrete products (a specific brand/product name, GTIN, ASIN, or attached image). "
            + "Do NOT extract collection names, category/theme names, or products named only as EXAMPLES "
            + "(introduced by 'e.g.', 'example', 'such as', 'like', 'for instance') — those are descriptive "
            + "context, not products to look up. If a brand and its collection appear together "
            + "(e.g. \"Xaia's Elemental Collection\"), extract only the brand/product (\"Xaia\"), not the collection.",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: ["gtin", "asin", "name", "attachment"],
                description: "'gtin' for numeric product IDs (12-14 digits), 'asin' for Amazon identifiers (B + 9 alphanumeric), 'name' for brand/product names (e.g., 'Doritos'), 'attachment' for user-attached images.",
              },
              value: {
                type: "string",
                description:
                  "ONLY the bare identifier — the GTIN digits, ASIN code, brand/product name, or attachment index. " +
                  "MUST NOT contain the surrounding user prompt or words like 'create', 'image', 'PDP', 'set', 'for', 'hero'. " +
                  "Example: for 'Create a PDP image for B07MGZ6X2Q' the value is 'B07MGZ6X2Q', NOT 'Create a PDP image for B07MGZ6X2Q'. " +
                  "Place the full prompt in `image_prompt` instead.",
              },
              display_name: {
                type: "string",
                description: "Human-readable label for this product. Same rules as `value` — never the full prompt.",
              },
              is_background: {
                type: "boolean",
                description:
                  "Set to true ONLY when the user explicitly wants this attachment used as the campaign BACKGROUND IMAGE " +
                  "(e.g., 'use this background image', 'using this as background'). " +
                  "You MUST set this flag — do NOT leave it out when the user is clearly referring to a background. " +
                  "A background image is not a product — it is a scene/environment behind the products.",
              },
            },
            required: ["type", "value"],
          },
        },
        text_elements: {
          type: "array",
          description:
            "Text elements to place on the banner. ONLY include if the user EXPLICITLY specified text content. " +
            "Do NOT invent or generate text that the user did not ask for.",
          items: {
            type: "object",
            properties: {
              content: { type: "string", description: "The exact text to display." },
              role: {
                type: "string",
                enum: ["headline", "subheadline", "cta", "body"],
                description: "The role of this text element.",
              },
              suggested_font_size_ratio: {
                type: "number",
                description: "Suggested font size as a ratio of template height (0.0-1.0). E.g., 0.08 for a headline.",
              },
              suggested_color: {
                type: "string",
                description:
                  "Suggested hex color for the text. Default to black (#000000) for readability. " +
                  "Use white (#FFFFFF) instead if background_scene indicates the area at suggested_position is dark " +
                  "(e.g., a dark sky in the top zone, a dark shadow in the bottom zone, or the scene is dark/night/low-light overall).",
              },
              suggested_position: {
                type: "string",
                enum: ["top-left", "top-center", "top-right", "center-left", "center", "center-right", "bottom-left", "bottom-center", "bottom-right"],
                description: "Suggested position zone for this text element. E.g., 'top-left' or 'top-center' for a headline.",
              },
            },
            required: ["content", "role"],
          },
        },
      },
      required: ["network_name"],
    },
  },
  {
    name: "create_template",
    description:
      "Returns layout sizes based on custom user input (e.g., 'create a 300x250 and 728x90 banner'). " +
      "Use this when the user provides specific sizes as text input, NOT when they mention a known network/campaign name. " +
      "If no resolution or size is specified, use 1024x1024 as the default. " +
      "MANDATORY: If the user asks for an image or to generate an image for a banner ad " +
      "(e.g., 'Campaign for back to school featuring Lays and Doritos chips'), " +
      "you MUST provide background_scene and product_references.",
    parameters: {
      type: "object",
      properties: {
        sizes: {
          type: "array",
          description: "Result sizes. If no size is specified by the user, use 1024x1024.",
          items: {
            type: "object",
            properties: {
              width: { type: "number" },
              height: { type: "number" },
            },
            required: ["width", "height"],
          },
        },
        image_prompt: {
          type: "string",
          description:
            "The full original user prompt for reference. The structured fields (background_scene, product_references, text_elements) are the decomposed version used for generation.",
        },
        background_scene: {
          type: "string",
          description:
            "A description of the background scene/environment ONLY. " +
            "Must NOT contain product names, brand names, or any specific product imagery. " +
            "Focus on theme, mood, colors, setting, atmosphere.",
        },
        product_references: {
          type: "array",
          description: "Products the user explicitly wants FEATURED as separate layers. "
            + "Extract ONLY concrete products (a specific brand/product name, GTIN, ASIN, or attached image). "
            + "Do NOT extract collection names, category/theme names, or products named only as EXAMPLES "
            + "(introduced by 'e.g.', 'example', 'such as', 'like', 'for instance') — those are descriptive "
            + "context, not products to look up. If a brand and its collection appear together "
            + "(e.g. \"Xaia's Elemental Collection\"), extract only the brand/product (\"Xaia\"), not the collection.",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: ["gtin", "asin", "name", "attachment"],
                description: "'gtin' for numeric product IDs, 'asin' for Amazon identifiers (B + 9 alphanumeric), 'name' for brand/product names, 'attachment' for user-attached images.",
              },
              value: {
                type: "string",
                description:
                  "ONLY the bare identifier — the GTIN digits, ASIN code, brand/product name, or attachment index. " +
                  "MUST NOT contain the surrounding user prompt or words like 'create', 'image', 'PDP', 'set', 'for', 'hero'. " +
                  "Example: for 'Create a PDP image for B07MGZ6X2Q' the value is 'B07MGZ6X2Q', NOT 'Create a PDP image for B07MGZ6X2Q'. " +
                  "Place the full prompt in `image_prompt` instead.",
              },
              display_name: {
                type: "string",
                description: "Human-readable label for this product. Same rules as `value` — never the full prompt.",
              },
            },
            required: ["type", "value"],
          },
        },
        text_elements: {
          type: "array",
          description:
            "Text elements to place on the banner. ONLY include if the user EXPLICITLY specified text content. " +
            "Do NOT invent or generate text that the user did not ask for.",
          items: {
            type: "object",
            properties: {
              content: { type: "string", description: "The exact text to display." },
              role: {
                type: "string",
                enum: ["headline", "subheadline", "cta", "body"],
                description: "The role of this text element.",
              },
              suggested_font_size_ratio: {
                type: "number",
                description: "Suggested font size as a ratio of template height (0.0-1.0).",
              },
              suggested_color: {
                type: "string",
                description: "Suggested hex color for the text.",
              },
              suggested_position: {
                type: "string",
                enum: ["top-left", "top-center", "top-right", "center-left", "center", "center-right", "bottom-left", "bottom-center", "bottom-right"],
                description: "Suggested position zone for this text element.",
              },
            },
            required: ["content", "role"],
          },
        },
      },
      required: ["sizes"],
    },
  },
  {
    name: "generate_design_concept",
    description:
      "Use when the user wants a design idea, creative idea, image concept, ad concept, banner concept, or standalone visual direction. " +
      "This includes vague requests like 'Give me a design idea' or 'Show me a concept'. " +
      "Do NOT use this for campaign creation, template creation, or image editing.",
    parameters: {
      type: "object",
      properties: {
        image_prompt: {
          type: "string",
          description:
            "A detailed image generation prompt describing the creative concept. " +
            "If the user is vague, expand their request into a strong visual concept with a clear composition and mood.",
        },
        image_prompts: {
          type: "array",
          items: { type: "string" },
          description: "Multiple concept prompts when distinct concept types are requested"
        },
        concept_summary: {
          type: "string",
          description:
            "A short plain-language summary of the idea, such as 'bold fall sports snack ad concept' or 'clean modern beauty promo concept'.",
        },
        size: {
          type: "object",
          description: "Single output size for the generated design concept.",
          properties: {
            width: { type: "number" },
            height: { type: "number" }
          },
          required: ["width", "height"]
        },
        use_attachments_as_reference: {
          type: "boolean",
          description:
            "Set to true when the user's attached images should be used as visual references, inspiration, style cues, or product references."
        },
        use_product_images: {
          type: "boolean",
          description: "Whether attached or named product images should be used as the main featured products in the concept."
        },
        product_image_names: {
          type: "array",
          items: { type: "string" },
          description: "Product image filenames explicitly mentioned by the user, such as 00028400096348.png."
        }
      },
      required: ["image_prompt"],
    }
  },
  {
    name: "create_pdp_image_set",
    description:
      "Create PDP (Product Detail Page) images. Use when the user wants to create PDP images, " +
      "a product page image set, 'how to' images, 'reason to believe' images, hero images, " +
      "or any product detail page content. " +
      "Recognizes prompts like 'Create a PDP image', 'Create a PDP image set', " +
      "'Create a how-to image for [product]', 'Create a reason to believe image for [ASIN]', " +
      "'Create a PDP image set for Salsify 1234567890', 'Create a PDP image for Salsify ID 1234567890', " +
      "'Create a PDP image for this product in Salsify 1234567890'. " +
      "ALSO recognizes 'based on' / 'using as reference' / 'in the style of' / 'featuring' prompts " +
      "that pair a target product with one or more reference products or reference images, e.g. " +
      "'Create a PDP image set for B01BOUS7AO based on the images in this product B078TZK3V3', " +
      "'Create images in a clean modern style featuring the product from ASIN B1234566790', " +
      "'Create a PDP image set for this product based on these reference images <attached>', " +
      "'Create a PDP image set for this product <attached> based on ASIN B0DG37PZLV'. " +
      "For these reference flows you MUST put the TARGET product in `product_references` and put the " +
      "SOURCE / inspiration product(s) in `reference_product_references` (NEVER in product_references); " +
      "and set `use_attachments_as_reference=true` whenever the user's attached images are the references " +
      "rather than the target product itself. " +
      "CRITICAL role rule: the TARGET is what the user creates the set FOR (the product named BEFORE 'based on', or an attached image the user calls 'this product' / 'this'); the REFERENCE is whatever comes AFTER 'based on' / 'using ... as reference' / 'in the style of' / 'featuring'. ANY product identifier (ASIN / GTIN / name / Salsify) that appears AFTER one of those phrases is a REFERENCE -> put it in `reference_product_references`, NEVER in `product_references`. When the user attaches an image and calls it 'this product' / 'this', that ATTACHMENT is the TARGET -> `product_references` with type 'attachment', and `use_attachments_as_reference` stays false. " +
      "Do NOT use for editing images — use edit_image instead.",
    parameters: {
      type: "object",
      properties: {
        is_set: {
          type: "boolean",
          description:
            "True when the user asks for multiple images (e.g., 'Create a PDP image set', " +
            "'Create multiple PDP images', 'Create a set of PDP images', 'Create several images'). " +
            "False when the user asks for a single image (e.g., 'Create a PDP image', " +
            "'Create a hero image', 'Make a reason to believe image'). " +
            "Infer from phrasing: plural nouns / words like 'set', 'multiple', 'several' → true; " +
            "singular 'a/an image' with no plural cue → false.",
        },
        image_types: {
          type: "array",
          items: { type: "string" },
          description:
            "Types of PDP images requested. Allowed values: " +
            "'hero', 'mobile_hero', 'mobile_hero_cambridge', 'mobile_hero_gs1', 'mobile_hero_reference', " +
            "'lifestyle', 'how_to', 'reason_to_believe', 'ingredients', 'nutrition_facts', " +
            "'feature_benefits', 'comparison', 'product_detail', " +
            "'product_gallery', 'cross_sell', 'category_gallery', 'feature_iconography_around', 'feature_iconography_bottom'. " +
            "New-type parsing rules: " +
            "'product gallery' → ['product_gallery']; 'cross-sell' / 'cross sell' → ['cross_sell']; " +
            "'category gallery' → ['category_gallery']; " +
            "'feature & iconography around the product' / 'benefit icons around the product' → ['feature_iconography_around']; " +
            "'feature & iconography at the bottom' / 'benefit icons below the image' → ['feature_iconography_bottom']. " +
            "Mobile hero parsing rules: " +
            "(a) If the user explicitly says 'Cambridge' near 'hero' / 'mobile hero' / 'mobile ready hero', emit 'mobile_hero_cambridge'. " +
            "(b) If the user says 'GS1' near hero terms, emit 'mobile_hero_gs1'. " +
            "(c) If the user attaches a reference image AND says 'like this', 'in this style', 'matching this image' (or similar) for a hero, emit 'mobile_hero_reference' and populate reference_images / product_references. " +
            "(d) If the user says 'mobile hero', 'mobile ready hero', or 'hero for mobile' WITHOUT a style word and WITHOUT a reference image, emit the generic 'mobile_hero' — the UI will ask the user to pick a style. " +
            "Examples: " +
            "'Create a Cambridge mobile ready hero for B0123' → ['mobile_hero_cambridge']. " +
            "'Create a GS1 hero image for SKU 7890' → ['mobile_hero_gs1']. " +
            "'Create a mobile ready hero for B0123' → ['mobile_hero']. " +
            "'Create a cross-sell image for B0123' → ['cross_sell']. " +
            "'Create a product gallery image for B0123' → ['product_gallery']. " +
            "'Create a feature & iconography image around the product for B0123' → ['feature_iconography_around']. " +
            "If unspecified, leave empty.",
        },
        product_references: {
          type: "array",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: ["gtin", "asin", "name", "attachment", "url", "salsify"],
                description:
                  "'gtin' for numeric product IDs (12-14 digits), 'asin' for Amazon identifiers " +
                  "(B + 9 alphanumeric), 'name' for brand/product names, 'attachment' for user-attached images, " +
                  "'salsify' for Salsify product IDs (e.g. 'Salsify 1234567890', 'Salsify ID 1234567890').",
              },
              value: {
                type: "string",
                description:
                  "ONLY the bare identifier — the GTIN digits, ASIN code, brand/product name, or attachment index. " +
                  "MUST NOT contain the surrounding user prompt or words like 'create', 'image', 'PDP', 'set', 'for', 'hero'. " +
                  "Example: for 'Create a PDP image set for B07MGZ6X2Q' the value is 'B07MGZ6X2Q', NOT the full sentence. " +
                  "Place the full prompt in `image_prompt` instead.",
              },
              display_name: {
                type: "string",
                description: "Human-readable label. Same rules as `value` — never the full prompt.",
              },
            },
            required: ["type", "value"],
          },
          description: "Product identifiers mentioned by the user.",
        },
        background_scene: {
          type: "string",
          description: "Background scene description if user specified one.",
        },
        image_prompt: {
          type: "string",
          description: "The full original user prompt for reference. Use this to capture specific descriptive intent, such as 'showing a 3 step process' or specific visual details.",
        },
        use_attachments_as_reference: {
          type: "boolean",
          description:
            "Set to true when the user's attached images should be used as REFERENCE images (style, " +
            "scene, inspiration) rather than as the target product itself. Typical triggers: " +
            "'based on these images', 'using these as reference', 'in this style', 'matching this look'. " +
            "Leave false / unset for normal PDP requests where attachments ARE the target product.",
        },
        reference_product_references: {
          type: "array",
          description:
            "Source / inspiration products to use as REFERENCE images for the generation (scenes, " +
            "backgrounds, style cues) — NOT the target product. Same shape as product_references. " +
            "Populate whenever the user says 'based on <product>', 'using <product> as reference', " +
            "'in the style of <product>', 'featuring the product from <ASIN>', or similar wording " +
            "that pairs a target product with one or more inspiration products. " +
            "Example: 'Create a PDP image set for B01 based on the images in this product B07' → " +
            "product_references=[{type:'asin', value:'B01'}], reference_product_references=[{type:'asin', value:'B07'}]. " +
            "Example (attached target + ASIN reference): 'Create a PDP image set for this product <attached> based on ASIN B0DG37PZLV' → " +
            "product_references=[{type:'attachment', value:'0'}], reference_product_references=[{type:'asin', value:'B0DG37PZLV'}], use_attachments_as_reference=false.",
          items: {
            type: "object",
            properties: {
              type: { type: "string", enum: ["asin", "gtin", "name", "url", "salsify"] },
              value: { type: "string" },
              display_name: { type: "string" },
            },
            required: ["type", "value"],
          },
        },
      },
      required: [],
    },
  },
  {
    name: "browse_templates",
    description:
      "Show available design templates for the user to browse. " +
      "Use when the user says 'show me templates', 'browse templates', 'template gallery', " +
      "'what templates are available', or similar requests about viewing pre-made design templates.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
  },
  {
    name: "download_last_image",
    description:
      "Download the most recently generated design concept or image in the chat. " +
      "Use when the user expresses a desire to save, download, or grab the last image produced.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
  },
  {
    name: "create_folder",
    description:
      "Create a new folder in the user's Files, Products, or Layouts section. " +
      "Use when the user says 'create a folder', 'add a folder', 'make a folder', or similar. " +
      "Do NOT use for creating layouts or campaigns.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
  },
  {
    name: "open_sell_sheet",
    description: "Open the Sell Sheet page when the user asks to create, make, open, or start a sell sheet.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
      additionalProperties: false
    }
  },
  {
    name: "open_file_browser_files",
    description: "Open the File Browser in Files mode when the user asks to upload files.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
      additionalProperties: false
    }
  },
  {
    name: "open_file_browser_products",
    description: "Open the File Browser with the Products tab selected when the user asks to upload products or upload product images.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
      additionalProperties: false
    }
  },
  {
    name: "translate_image",
    description:
      "Translate all text in an image to one or more target languages. " +
      "Call this whenever the user wants to translate an image. " +
      "Extract languages from the message into target_languages. " +
      "If no languages are specified, pass an empty array — the handler will ask the user. " +
      "Do NOT respond with plain text when a translation request is detected — always call this function.",
    parameters: {
      type: "object",
      properties: {
        target_languages: {
          type: "array",
          items: { type: "string" },
          description:
            "Languages to translate the image text to, e.g. ['French', 'German', 'Spanish']. " +
            "May be empty if the user did not specify languages.",
        },
      },
      required: [],
      additionalProperties: false,
    },
  },
  {
    name: "import_psd",
    description:
      "Import a PSD (Photoshop) file into a new layout. " +
      "Use when the user asks to import a PSD, import from Photoshop, or upload a PSD file. " +
      "Do not reply with plain text — always call this function.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
      additionalProperties: false,
    },
  },
  {
    name: "upload_chat_attachments_to_folder",
    description: "Open the folder picker UI for attached chat files. Use this whenever the user wants to upload attached files or images. Do not ask the user to type a folder name in chat.",
    parameters: {
      type: "object",
      properties: {
        mode: {
          type: "string",
          enum: ["files", "products"]
        },
        attachment_indexes: {
          type: "array",
          items: { type: "integer" }
        }
      },
      required: ["mode", "attachment_indexes"],
      additionalProperties: false
    }
  },
  {
    name: "generate_ab_hero_test",
    description:
      "Open the A/B hero experiment form. Use when the user wants to generate two hero " +
      "image variations from one source product to A/B test (e.g. 'generate two hero images and " +
      "A/B test them', 'make two hero variants to test in Optix', 'A/B test two product heroes'). " +
      "Do NOT use this for a single PDP image or a PDP image set — use create_pdp_image_set for those.",
    parameters: {
      type: "object",
      properties: {
        source_product_reference: {
          type: "string",
          description:
            "Optional. The source product the user named (ASIN, GTIN, Salsify ID, or product name) " +
            "to pre-fill the Source row. Omit if the user did not name a product.",
        },
      },
      required: [],
    },
  },
  {
    name: 'create_pdp_layout_with_proof',
    description:
      'Creates a PDP layout from the generated images and automatically generates a shareable proof link. ' +
      'Use when the user asks to create a layout AND wants a proof or review link to share (e.g. include in an email or Slack message).',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
];
